import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  TrendingUp,
  TrendingDown,
  Remove as HoldIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import MobileStockCard from '../components/shared/MobileStockCard';

const Watchlist = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const showOnboarding = location.state?.showOnboarding || false;
  const [watchlistData, setWatchlistData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [showGettingStarted, setShowGettingStarted] = useState(showOnboarding);
  const [newStock, setNewStock] = useState({ symbol: '', company_name: '' });
  const [addingStock, setAddingStock] = useState(false);
  const [groupBy, setGroupBy] = useState('sector'); // 'sector', 'signal', 'none'
  const [sortField, setSortField] = useState('symbol');
  const [sortDirection, setSortDirection] = useState('asc');

  const SIGNAL_ORDER = { 'SELL': 1, 'STRONG_SELL': 2, 'BUY': 3, 'STRONG_BUY': 4, 'HOLD': 5 };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (item, field) => {
    switch (field) {
      case 'symbol': return item.symbol || '';
      case 'company': return item.company_name || item.security || '';
      case 'market_cap': return parseFloat(item.market_cap) || 0;
      case 'signal': return SIGNAL_ORDER[(item.final_signal || item.signal)?.toUpperCase()] || 99;
      case 'confidence': return parseFloat(item.confidence) || 0;
      case 'expected_return': return parseFloat(item.expected_return) || 0;
      default: return '';
    }
  };

  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  };

  const getGrouped = () => {
    if (groupBy === 'none') {
      return { 'All Stocks': sortItems(watchlistData) };
    }

    const key = groupBy === 'sector'
      ? (item) => item.sector || 'Other'
      : (item) => (item.final_signal || item.signal)?.toUpperCase() || 'No Signal';

    const grouped = watchlistData.reduce((acc, item) => {
      const group = key(item);
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});

    // Sort items within each group
    Object.keys(grouped).forEach(g => { grouped[g] = sortItems(grouped[g]); });

    return grouped;
  };

  const getSortedGroupKeys = (grouped) => {
    return Object.keys(grouped).sort((a, b) => {
      if (a === 'Other' || a === 'No Signal' || a === 'All Stocks') return 1;
      if (b === 'Other' || b === 'No Signal' || b === 'All Stocks') return -1;
      return a.localeCompare(b);
    });
  };

  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/watchlist');
      
      if (response.data.success) {
        setWatchlistData(response.data.data);
        setError('');
      } else {
        setError('Failed to fetch watchlist data');
      }
    } catch (err) {
      console.error('Error fetching watchlist:', err);
      setError('Failed to fetch watchlist data');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    try {
      const response = await axios.delete(`/watchlist/${symbol}`);
      
      if (response.data.success) {
        setWatchlistData(prev => prev.filter(item => item.symbol !== symbol));
      } else {
        setError('Failed to remove stock from watchlist');
      }
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      setError('Failed to remove stock from watchlist');
    }
  };

  const handleAddToWatchlist = async () => {
    if (!newStock.symbol.trim()) {
      setError('Please enter a stock symbol');
      return;
    }

    try {
      setAddingStock(true);
      const response = await axios.post('/watchlist/add', {
        symbol: newStock.symbol.toUpperCase(),
        company_name: newStock.company_name
      });
      
      if (response.data.success) {
        setAddDialogOpen(false);
        setNewStock({ symbol: '', company_name: '' });
        fetchWatchlist(); // Refresh the list
      } else {
        setError(response.data.message || 'Failed to add stock to watchlist');
      }
    } catch (err) {
      console.error('Error adding to watchlist:', err);
      setError(err.response?.data?.message || 'Failed to add stock to watchlist');
    } finally {
      setAddingStock(false);
    }
  };

  const getSignalColor = (signal) => {
    switch (signal?.toUpperCase()) {
      case 'STRONG_BUY':
        return 'success';
      case 'BUY':
        return 'primary';
      case 'HOLD':
        return 'warning';
      case 'SELL':
        return 'error';
      case 'STRONG_SELL':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSignalIcon = (signal) => {
    switch (signal?.toUpperCase()) {
      case 'STRONG_BUY':
      case 'BUY':
        return <TrendingUp />;
      case 'HOLD':
        return <HoldIcon />;
      case 'SELL':
      case 'STRONG_SELL':
        return <TrendingDown />;
      default:
        return null;
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength?.toUpperCase()) {
      case 'VERY_STRONG':
        return '#2e7d32'; // Dark green — strong conviction
      case 'STRONG':
        return '#4caf50'; // Green — solid signal
      case 'MODERATE':
        return '#ff9800'; // Orange — close to signal change
      case 'WEAK':
        return '#ef6c00'; // Dark orange — mixed signals
      case 'VERY_WEAK':
        return '#9e9e9e'; // Grey — bearish lean or neutral
      default:
        return '#9e9e9e';
    }
  };

  useEffect(() => {
    fetchWatchlist();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2, borderRadius: 2, width: '40%' }} />
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Getting Started Banner for new users */}
      {(showGettingStarted || watchlistData.length === 0) && !loading && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
          onClose={watchlistData.length > 0 ? () => setShowGettingStarted(false) : undefined}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            👋 Welcome to SMPL Analysis!
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Here's how to get started in 3 steps:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mb: 1, '& li': { mb: 0.5 } }}>
            <li><strong>Add stocks</strong> to your Watchlist using the "Add Stock" button above</li>
            <li><strong>System analyzes</strong> your stocks daily — generating BUY/SELL/HOLD signals with AI-powered analysis</li>
            <li><strong>Automated trading</strong> executes based on signal confidence (you'll receive email alerts for every trade)</li>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Tip: Start with stocks you're interested in (e.g., AAPL, MSFT, GOOGL, TSLA). The system will fetch 5 years of data and start generating signals within minutes.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Watchlist & Trading Signals
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchWatchlist}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Stock
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {watchlistData.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="body2" color="textSecondary">Group by:</Typography>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(e, val) => { if (val) setGroupBy(val); }}
            size="small"
          >
            <ToggleButton value="sector">Sector</ToggleButton>
            <ToggleButton value="signal">Signal</ToggleButton>
            <ToggleButton value="none">None</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {watchlistData.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Your watchlist is empty
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Add stocks to your watchlist to track their trading signals
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Your First Stock
            </Button>
          </CardContent>
        </Card>
      ) : (
        (() => {
          const grouped = getGrouped();
          const sortedKeys = getSortedGroupKeys(grouped);

          const sortableHeader = (field, label) => (
            <TableCell sortDirection={sortField === field ? sortDirection : false}>
              <TableSortLabel
                active={sortField === field}
                direction={sortField === field ? sortDirection : 'asc'}
                onClick={() => handleSort(field)}
              >
                <strong>{label}</strong>
              </TableSortLabel>
            </TableCell>
          );

          return sortedKeys.map((group) => (
            <Box key={group} sx={{ mb: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 0,
                  backgroundColor: 'action.selected',
                  borderRadius: '4px 4px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: 1,
                  borderColor: 'divider',
                  borderBottom: 0,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {group}
                </Typography>
                <Chip
                  label={`${grouped[group].length} stock${grouped[group].length > 1 ? 's' : ''}`}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Paper>

              {/* Mobile: Card layout */}
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  {grouped[group].map((item, index) => (
                    <MobileStockCard
                      key={`${item.symbol}-${index}`}
                      symbol={item.symbol}
                      name={item.company_name || item.security || ''}
                      signal={(item.final_signal || item.signal || '').replace('_', ' ')}
                      confidence={item.confidence}
                      strategy={item.strategy_used}
                      onClick={() => window.location.href = `/stock/${item.symbol}`}
                      actions={
                        <IconButton color="error" onClick={(e) => { e.stopPropagation(); handleRemoveFromWatchlist(item.symbol); }} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    />
                  ))}
                </Box>
              ) : (
              /* Desktop: Table layout */
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: '0 0 4px 4px' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      {sortableHeader('symbol', 'Symbol')}
                      {sortableHeader('company', 'Company')}
                      {sortableHeader('market_cap', 'Market Cap')}
                      {sortableHeader('signal', 'Signal')}
                      {sortableHeader('confidence', 'Confidence')}
                      <TableCell><strong>Strength</strong></TableCell>
                      <TableCell><strong>Strategy</strong></TableCell>
                      <TableCell><strong>Signal Date</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped[group].map((item, index) => (
                <TableRow key={`${item.symbol}-${index}`} hover>
                  <TableCell>
                    <Link 
                      to={`/stock/${item.symbol}`} 
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography 
                        variant="h6" 
                        color="primary"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {item.symbol}
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.company_name || item.security || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.market_cap ? (
                      <Typography variant="body2">
                        ${(item.market_cap / 1000000000).toFixed(1)}B
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(item.final_signal || item.signal) ? (
                      <Box>
                        <Chip
                          icon={getSignalIcon(item.final_signal || item.signal)}
                          label={(item.final_signal || item.signal).replace('_', ' ')}
                          color={getSignalColor(item.final_signal || item.signal)}
                          variant="filled"
                          size="small"
                        />
                        {item.final_signal && item.signal && item.final_signal !== item.signal && (
                          <Tooltip title={`Technical: ${item.signal} → AI: ${item.ai_recommendation || 'N/A'} → Final: ${item.final_signal}`}>
                            <Chip
                              label="🤖 AI Override"
                              size="small"
                              variant="outlined"
                              sx={{ ml: 0.5, fontSize: '0.7rem', height: 20 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No signal
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.confidence ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">
                          {(parseFloat(item.confidence) * 100).toFixed(1)}%
                        </Typography>
                        <Box
                          sx={{
                            width: 50,
                            height: 6,
                            backgroundColor: '#e0e0e0',
                            borderRadius: 3,
                            ml: 1,
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(parseFloat(item.confidence) * 100)}%`,
                              height: '100%',
                              backgroundColor: (parseFloat(item.confidence) * 100) >= 80 ? '#4caf50' : 
                                             (parseFloat(item.confidence) * 100) >= 60 ? '#ff9800' : '#f44336',
                              borderRadius: 3
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.strength ? (
                      <Chip
                        label={item.strength.replace('_', ' ')}
                        size="small"
                        sx={{
                          backgroundColor: getStrengthColor(item.strength),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.strategy_used ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {item.strategy_used.replace('_', ' ')}
                        </Typography>
                        {item.expected_return !== null && item.expected_return !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: parseFloat(item.expected_return) >= 0 ? '#4caf50' : '#f44336',
                              fontWeight: 'bold'
                            }}
                          >
                            Backtest: {parseFloat(item.expected_return) >= 0 ? '+' : ''}{parseFloat(item.expected_return).toFixed(2)}%
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.signal_date ? (
                      <Typography variant="body2">
                        {new Date(item.signal_date).toLocaleDateString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Remove from watchlist">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveFromWatchlist(item.symbol)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
          ));
        })()
      )}

      {/* Add Stock Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Stock to Watchlist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Stock Symbol"
            type="text"
            fullWidth
            variant="outlined"
            value={newStock.symbol}
            onChange={(e) => setNewStock(prev => ({ ...prev, symbol: e.target.value.toUpperCase() }))}
            placeholder="e.g., AAPL"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Company Name (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={newStock.company_name}
            onChange={(e) => setNewStock(prev => ({ ...prev, company_name: e.target.value }))}
            placeholder="e.g., Apple Inc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddToWatchlist} 
            variant="contained"
            disabled={addingStock}
          >
            {addingStock ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' }
        }}
        onClick={() => setAddDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default Watchlist;
